import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
	Grid,
	TextField,
	MenuItem,
	FormControl,
	InputAdornment,
	IconButton,
	Tooltip,
} from '@mui/material';
import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';

import { updateLocaleField } from 'common/utils/languageUtils';
import { PricingItem, CurrencyObject } from 'common/types';
import { DAYS } from 'common/constants/timePeriods';
import MoneyTextField from 'common/components/MoneyTextField';
import useFocusWithin from 'common/hooks/useFocusWithin';
import TimeValueSelect from 'views/Product/__components/TimeValueSelect';
import TimePeriodSelect from 'views/Product/__components/TimePeriodSelect';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useTranslation } from 'services/localization/useTranslation';
import { getDurationLabel } from 'common/constants/timePeriods';

interface Props {
	item: PricingItem;
	index: number;
	onChange: (index: number, value: PricingItem) => void;
	onDelete: (index: number) => void;
	basePrice: number;
	currency: CurrencyObject;
	locked?: boolean;
	hidePrice?: boolean;
	hideMultiplier?: boolean;
	priceIsExample?: boolean;
}

const getMultiplierFromPrice = (price: number, basePrice: number | undefined) => {
	return basePrice ? price / basePrice : 0;
};

const getPriceFromMultiplier = (multiplier: number, basePrice: number | undefined) => {
	return basePrice ? Math.round(basePrice * multiplier) : 0;
};

const LabelInput = React.memo(
	(props: {
		value: PricingItem['label'];
		timePeriod: PricingItem['timePeriod'];
		timeValue: PricingItem['timeValue'];
		locked: boolean;
		editItem: (changes: Partial<PricingItem>) => void;
	}) => {
		const inputRef = useRef<HTMLInputElement>(null);
		const [isEditing, setIsEditing] = useState(false);
		const { t } = useTranslation();
		const shopDefaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);

		const getDefaultValue = () => {
			return getDurationLabel(props.timePeriod, props.timeValue || 0, t);
		};

		const getLabel = () => {
			if (props.value?.def) {
				return t('pricing.rowTitleCustom', 'Custom title');
			} else {
				return t('pricing.rowTitle', 'Title (using default)');
			}
		};

		const getValue = () => {
			if (isEditing) {
				return props.value?.def ?? '';
			} else {
				return props.value?.def || getDefaultValue();
			}
		};

		useEffect(() => {
			if (isEditing) {
				inputRef.current?.focus();
			}
		}, [isEditing]);

		const inputProps =
			!isEditing && !props.locked
				? {
						endAdornment: (
							<InputAdornment position="end">
								<Tooltip title={t('pricing.editTitle', 'Edit title')}>
									<IconButton onClick={() => setIsEditing(true)} size="large">
										<EditIcon />
									</IconButton>
								</Tooltip>
							</InputAdornment>
						),
				  }
				: {};

		const value = getValue();

		return (
			<Grid item xs>
				<Tooltip
					open={isEditing && value.length > 0}
					title={t(
						'pricing.customTitleWarning',
						'Please note that custom titles are not automatically translated to other languages. You can manage the translations of this label under Account > Languages.',
					)}
					placement="top"
					TransitionProps={{
						timeout: 0,
					}}
				>
					<TextField
						inputRef={inputRef}
						fullWidth
						label={getLabel()}
						InputLabelProps={{
							shrink: true,
						}}
						value={value}
						placeholder={getDefaultValue()}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
							props.editItem({
								label: updateLocaleField(props.value, e.target.value, shopDefaultLanguage),
							})
						}
						variant="outlined"
						margin="normal"
						type="text"
						disabled={props.locked || !isEditing}
						InputProps={inputProps}
						inputProps={{
							onBlur: () => setIsEditing(false),
						}}
					/>
				</Tooltip>
			</Grid>
		);
	},
);

const TimeValueInput = React.memo(
	(props: {
		value: PricingItem['timeValue'];
		locked: boolean;
		editItem: (changes: Partial<PricingItem>) => void;
	}) => (
		<Grid item xs={1}>
			<TimeValueSelect
				value={props.value ?? ''}
				onChange={(timeValue) =>
					props.editItem({
						timeValue,
					})
				}
				textFieldProps={{ disabled: props.locked, variant: 'outlined' }}
			/>
		</Grid>
	),
);

const TimePeriodInput = React.memo(
	(props: {
		value: PricingItem['timePeriod'];
		timeValue: PricingItem['timeValue'];
		locked: boolean;
		editItem: (changes: Partial<PricingItem>) => void;
	}) => {
		const { t } = useTranslation();
		return (
			<Grid item xs={2}>
				<TimePeriodSelect
					value={props.value === 'days' ? DAYS : props.value}
					onChange={(timePeriod) =>
						props.editItem({
							timePeriod: timePeriod as PricingItem['timePeriod'],
						})
					}
					labelFormat={props.timeValue === 1 ? 'singular' : 'plural'}
					disableNone
					textFieldProps={{
						label: t('common:times.period', 'Period'),
						margin: 'normal',
						disabled: props.locked,
						variant: 'outlined',
					}}
				/>
			</Grid>
		);
	},
);

const MultiplierInput = React.memo(
	(props: {
		value: PricingItem['multiplier'];
		price: PricingItem['price'];
		basePrice: number | undefined;
		locked: boolean;
		editItem: (changes: Partial<PricingItem>) => void;
		large?: boolean;
	}) => {
		const { t } = useTranslation();

		const handleChange = (multiplier: number) => {
			const price = getPriceFromMultiplier(multiplier, props.basePrice);

			props.editItem({
				multiplier,
				price,
			});
		};

		return (
			<Grid item xs={props.large ? 3 : 1}>
				<TextField
					label={t('product.multiplier', 'Multiplier')}
					value={
						props.value ??
						(props.price !== '' ? getMultiplierFromPrice(props.price, props.basePrice) : '')
					}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
						const multiplier = parseFloat(e.target.value);
						handleChange(multiplier);
					}}
					onBlur={() => {
						if (!props.value || isNaN(props.value)) {
							handleChange(0);
						}
					}}
					margin="normal"
					variant="outlined"
					type="number"
					inputProps={{
						min: 0,
					}}
					disabled={props.locked}
				/>
			</Grid>
		);
	},
);

const PriceInput = React.memo(
	(props: {
		value: PricingItem['price'];
		basePrice: number | undefined;
		locked: boolean;
		currency: CurrencyObject;
		editItem: (changes: Partial<PricingItem>) => void;
		large?: boolean;
		example?: boolean;
	}) => {
		const { t } = useTranslation();
		return (
			<Grid item xs={props.large ? 3 : 2}>
				<MoneyTextField
					label={
						props.example ? t('pricing.examplePrice', 'Example price') : t('pricing.price', 'Price')
					}
					value={props.value !== '' ? props.value : undefined}
					onValueChange={(price) => {
						const multiplier = getMultiplierFromPrice(price, props.basePrice);
						props.editItem({
							price,
							multiplier,
						});
					}}
					placeholder={t('pricing.free', 'Free')}
					InputLabelProps={{
						shrink: true,
					}}
					useMinorUnits
					currency={props.currency}
					margin="normal"
					variant="outlined"
					fullWidth
					disabled={props.locked || props.basePrice === 0}
				/>
			</Grid>
		);
	},
);

const ChannelsInput = React.memo(
	(props: {
		value: PricingItem['channels'];
		locked: boolean;
		editItem: (changes: Partial<PricingItem>) => void;
	}) => {
		const { t } = useTranslation();
		const classes = useStyles();

		const renderValue = (_value: any) => {
			const value = _value as string[];
			if (value.includes('ALL')) {
				return (
					<span className={classes.greenText}>{t('product.allChannels', 'All Channels')}</span>
				);
			}

			if (value.includes('HIDDEN')) {
				return <span className={classes.redText}>{t('product.hiddenChannel', 'Hidden')}</span>;
			}

			return (
				<span>
					{value
						.map((value) => {
							switch (value) {
								case 'ADMIN':
									return t('product.adminChannel', 'Admin');
								case 'ONLINE':
									return t('product.onlineChannel', 'Online');
								case 'STORE':
									return t('product.selfServiceChannel', 'Self-Service');
								default:
									return value;
							}
						})
						.join(', ')}
				</span>
			);
		};

		const handleChange = (e: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
			const channels = e.target.value as NonNullable<PricingItem['channels']>;
			const lastAddedItem = channels[channels.length - 1];

			if (lastAddedItem === 'ALL') {
				props.editItem({
					channels: ['ALL'],
				});
			} else if (lastAddedItem === 'HIDDEN') {
				props.editItem({
					channels: ['HIDDEN'],
				});
			} else {
				const filtered = channels.filter((c) => c !== 'ALL' && c !== 'HIDDEN');
				if (filtered.length === 3) {
					props.editItem({
						channels: ['ALL'],
					});
				} else {
					props.editItem({
						channels: filtered,
					});
				}
			}
		};

		return (
			<Grid item xs={2}>
				<TextField
					select
					margin="normal"
					label={t('shop.channels', 'Channels')}
					variant="outlined"
					fullWidth
					disabled={props.locked}
					SelectProps={{
						multiple: true,
						renderValue,
					}}
					value={props.value && props.value.length ? props.value : ['ALL']}
					onChange={handleChange}
				>
					<MenuItem className={classes.greenText} value={'ALL'}>
						{t('product.allChannels', 'All Channels')}
					</MenuItem>
					<MenuItem value={'ADMIN'}>{t('product.adminChannel', 'Admin')}</MenuItem>
					<MenuItem value={'ONLINE'}>{t('product.onlineChannel', 'Online')}</MenuItem>
					<MenuItem value={'STORE'}>{t('product.selfServiceChannel', 'Self-Service')}</MenuItem>
					<MenuItem className={classes.redText} value={'HIDDEN'}>
						{t('product.hiddenChannel', 'Hidden')}
					</MenuItem>
				</TextField>
			</Grid>
		);
	},
);

const noOp = () => {};
const PricingTableRow = (props: Props) => {
	const {
		onChange,
		onDelete,
		index,
		basePrice,
		currency,
		item,
		hideMultiplier,
		hidePrice,
		priceIsExample,
	} = props;
	const { label, timeValue, timePeriod, multiplier, channels } = item;
	// Price is currently always calculated from multiplier, to use same logic as how pricing is calculated on client facing channels
	const price = getPriceFromMultiplier(multiplier || 0, basePrice);
	const [hasFocus, _, focusProps] = useFocusWithin();

	const editItem = useCallback(
		(changes: Partial<PricingItem>) => {
			onChange(index, {
				...item,
				...changes,
			});
		},
		[item, onChange, index],
	);

	const handleDelete = useCallback(() => {
		onDelete(index);
	}, [index, onDelete]);

	const sharedProps = {
		editItem: hasFocus ? editItem : noOp,
		locked: props.locked ?? false,
	};

	return (
		<Grid container spacing={2} {...focusProps}>
			<LabelInput value={label} timeValue={timeValue} timePeriod={timePeriod} {...sharedProps} />
			<TimeValueInput value={timeValue} {...sharedProps} />
			<TimePeriodInput value={timePeriod} timeValue={timeValue} {...sharedProps} />
			{!hideMultiplier && (
				<MultiplierInput
					value={multiplier}
					price={price}
					basePrice={basePrice}
					large={hidePrice}
					{...sharedProps}
				/>
			)}
			{!hidePrice && (
				<PriceInput
					value={price}
					basePrice={basePrice}
					currency={currency}
					large={hideMultiplier}
					example={priceIsExample}
					{...sharedProps}
				/>
			)}
			<ChannelsInput value={channels} {...sharedProps} />
			{!props.locked && (
				<Grid item xs={1}>
					<FormControl margin="normal">
						<IconButton onClick={handleDelete} size="large">
							<ClearIcon />
						</IconButton>
					</FormControl>
				</Grid>
			)}
		</Grid>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		greenText: {
			color: theme.palette.success.main,
		},
		redText: {
			color: theme.palette.error.main,
		},
	}),
);

export default PricingTableRow;
